import React, { useState, useEffect, useCallback } from "react";
import { Grid, CardHeader, CardContent, Box, TextField, Typography, Avatar, Chip, Divider, Paper, Alert } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { teamManagementApi } from "../../services/AllServices";
import  Iconify  from "../../components/iconify";
import * as Yup from "yup";

function EditPlayer() {
  const navigate = useNavigate();
  const { id, playerteamid } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const location = useLocation();
  const { leagueData } = location.state || {};
  const [isPriceFocused, setIsPriceFocused] = useState(false);

  const positionNames = {
    24: "Goalkeeper",
    25: "Defender",
    26: "Midfielder",
    27: "Forward"
  };

  const fetchPlayerData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await teamManagementApi.getById({ id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  const validationSchema = Yup.object().shape({
    price: Yup.number()
      .typeError("Price must be a number")
      .min(0.1, "Price must be at least 0.1")
      .test('maxDigits', 'Price cannot exceed 12 digits',
        value => value ? value.toString().replace('.', '').length <= 12 : true
      )
      .required("Price is required"),
  });

  useEffect(() => {
    fetchPlayerData();
  }, [fetchPlayerData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      displayName: data?.displayName || "",
      teamName: data?.leagueTeams?.find(team => team.teamId === String(playerteamid))?.teamName || "-" || "",
      price: (data?.price !== undefined && data?.price !== null) ? String(data.price) : "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          id,
          price: values.price === "" ? null : Number(values.price),
        };
        const response = await teamManagementApi.edit(payload);
        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const formatPriceWithDecimals = (value) => {
    if (!value || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num)) return value;
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const handlePriceChange = (event) => {
    let raw = event.target.value;

    // Remove commas and other formatting characters
    raw = raw.replace(/[^0-9.]/g, "");

    // Allow empty input (so users can clear the field) and a trailing decimal like "12."
    if (raw === "") {
      formik.setFieldValue("price", "");
      return;
    }

    // Accept only digits and optional single decimal point
    if (!/^\d*\.?\d*$/.test(raw)) return;

    // Limit to 2 decimal places
    if (raw.includes('.')) {
      const [intPart, decPart] = raw.split('.');
      if (decPart && decPart.length > 2) return;
    }

    // Limit total digits (excluding the decimal point) to 12
    const digitsOnlyLength = raw.replace('.', '').length;
    if (digitsOnlyLength > 12) return;

    formik.setFieldValue("price", raw);
  };

  const handlePriceFocus = () => {
    setIsPriceFocused(true);
  };

  const handlePriceBlur = () => {
    setIsPriceFocused(false);
  };

  const { values, touched, errors, handleChange, handleSubmit } = formik;

  const PlayerInfoCard = () => (
    <Paper 
      elevation={0}
      className="edit-player-info-card"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
        <Avatar
          src={data?.imagePath}
          alt={data?.fullName || "Player"}
          className="edit-player-avatar"
          sx={{
            width: 80,
            height: 80,
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://cdn.sportmonks.com/images/soccer/placeholder.png";
          }}
        />
        <Box>
          <Typography variant="h5" className="edit-player-name" sx={{ fontWeight: 700, mb: 1 }}>
            {data?.fullName || "Player Name"}
          </Typography>
          <Typography variant="body1" className="edit-player-display-name" sx={{ mb: 1 }}>
            {data?.displayName || "Display Name"}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data?.positionId && (
              <Chip
                label={positionNames[data.positionId] || data.positionId}
                color="primary"
                variant="outlined"
                size="small"
                icon={<Iconify icon="eva:shield-outline" />}
              />
            )}
            <Chip
              label={data?.status === 1 ? "Active" : "Inactive"}
              color={data?.status === 1 ? "success" : "error"}
              variant="outlined"
              size="small"
              icon={<Iconify icon={data?.status === 1 ? "eva:checkmark-circle-outline" : "eva:close-circle-outline"} />}
            />
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3, opacity: 0.3 }} />
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="edit-player-info-grid" sx={{ textAlign: 'center', p: 2 }}>
            <Iconify icon="eva:people-outline" className="edit-player-info-icon" sx={{ fontSize: 24, mb: 1 }} />
            <Typography variant="caption" className="edit-player-info-label" sx={{ display: 'block', mb: 0.5 }}>
              TEAM
            </Typography>
            <Typography variant="body2" className="edit-player-info-value" sx={{ fontWeight: 600 }}>
              {data?.leagueTeams?.find(team => team.teamId === String(playerteamid))?.teamName || "-"}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Box className="edit-player-info-grid" sx={{ textAlign: 'center', p: 2 }}>
            <Iconify icon="eva:flag-outline" className="edit-player-info-icon" sx={{ fontSize: 24, mb: 1 }} />
            <Typography variant="caption" className="edit-player-info-label" sx={{ display: 'block', mb: 0.5 }}>
              COUNTRY
            </Typography>
            <Typography variant="body2" className="edit-player-info-value" sx={{ fontWeight: 600 }}>
              {leagueData?.country || "-"}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Box className="edit-player-info-grid" sx={{ textAlign: 'center', p: 2 }}>
            <Iconify icon="eva:star-outline" className="edit-player-info-icon" sx={{ fontSize: 24, mb: 1 }} />
            <Typography variant="caption" className="edit-player-info-label" sx={{ display: 'block', mb: 0.5 }}>
              LEAGUE
            </Typography>
            <Typography variant="body2" className="edit-player-info-value" sx={{ fontWeight: 600 }}>
              {leagueData?.name || "-"}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Box className="edit-player-info-grid" sx={{ textAlign: 'center', p: 2 }}>
            <Iconify icon="eva:star-outline" className="edit-player-info-icon" sx={{ fontSize: 24, mb: 1 }} />
            <Typography variant="caption" className="edit-player-info-label" sx={{ display: 'block', mb: 0.5 }}>
              GAME POINTS
            </Typography>
            <Typography variant="body2" className="edit-player-info-value" sx={{ fontWeight: 600 }}>
              {data?.gamePoints || "-"}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const EditFormCard = () => (
    <Paper 
      elevation={0}
      className="edit-player-form-card"
      sx={{ p: 3 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Iconify icon="eva:edit-outline" sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h6" className="edit-player-form-header" sx={{ fontWeight: 600 }}>
          Edit Player Information
        </Typography>
      </Box>
      
      <Alert severity="info" className="edit-player-alert" sx={{ mb: 3 }}>
        <Typography variant="body2">
          You can only edit the player's price. Other information is read-only for data integrity.
        </Typography>
      </Alert>
      
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Player Name"
                name="displayName"
                value={values.displayName}
                disabled
                className="edit-player-input-disabled"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Team Name"
                name="teamName"
                value={values?.teamName}
                disabled
                className="edit-player-input-disabled"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country Name"
                name="country"
                value={leagueData?.country}
                disabled
                className="edit-player-input-disabled"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="League Name"
                name="leaguename"
                value={leagueData?.name}
                disabled
                className="edit-player-input-disabled"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                value={values.price}
                onChange={handlePriceChange}
                type="text"
                autoFocus
                required
                inputMode="decimal"
                inputProps={{ inputMode: 'decimal', pattern: "[0-9]*[.,]?[0-9]*" }}
                error={Boolean(touched.price && errors.price)}
                helperText={touched.price && errors.price}
                className="edit-player-input-editable"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={loading}
                  className="edit-player-button-primary"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    transition: 'all 0.3s ease'
                  }}
                >
                  Update Player
                </LoadingButton>
                
                <LoadingButton
                  size="large"
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  className="edit-player-button-secondary"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    transition: 'all 0.3s ease'
                  }}
                >
                  Cancel
                </LoadingButton>
              </Box>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </Paper>
  );

  return (
    <Box>
      <Helmet>
        <title>Edit Player</title>
      </Helmet>
      <CardLayout>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Iconify icon="eva:edit-outline" sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Edit Player
              </Typography>
            </Box>
          }
        />
        <CardContent>
          {loading && !data ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Iconify icon="eva:loading-outline" sx={{ fontSize: 48, color: 'primary.main', animation: 'spin 1s linear infinite' }} />
              <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                Loading player information...
              </Typography>
            </Box>
          ) : (
            <>
              <PlayerInfoCard />
              <EditFormCard />
            </>
          )}
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default EditPlayer;